import React, { FC } from "react";
import {
  ClientFilterData,
  ClientValue,
  ExternalMatterData,
  Matter,
  MatterFormMode,
  MatterTemplate,
  MatterType,
  OnClientSave,
  OnMatterUpdate,
  PracticeArea,
} from "../../data-types";
import { MatterEditorGenericTab } from "../matter-editor-generic-tab";
import { MatterDetailsEditor } from "./matter-details-editor";

import styles from "./matter-client-and-matter-details.css";
import { Spinner } from "../spinner";

const { ["wrapper"]: wrapper } = styles as any;

type MatterClientAndMatterDetailsSharedProps<T extends MatterType, U extends MatterFormMode> = {
  pmsName: string;
  practiceAreas: PracticeArea[];
  onMatterUpdate: OnMatterUpdate<T, U>;
  feeEarnersCascaderRender: () => React.ReactNode;
  client?: ClientValue | null;
  setCurrentClient?: (client: ClientValue | undefined | null) => void;
  updateMatterLoading?: boolean;
};

type MatterClientAndMatterDetailsTemplateProps<
  T extends MatterType,
  U extends MatterFormMode
> = MatterClientAndMatterDetailsSharedProps<T, U> & {
  matter: MatterTemplate;
  externalMatterData?: never;
  onClientSave?: never;
  clientFilterData?: never;
  client?: never;
  setCurrentClient?: never;
};

type MatterClientAndMatterDetailsMatterProps<
  T extends MatterType,
  U extends MatterFormMode
> = MatterClientAndMatterDetailsSharedProps<T, U> & {
  matter: Matter;
  externalMatterData: ExternalMatterData;
  onClientSave: OnClientSave;
  clientFilterData: ClientFilterData;
};

type MatterClientAndMatterDetailsProps<
  T extends MatterType,
  U extends MatterFormMode
> = T extends "template"
  ? MatterClientAndMatterDetailsTemplateProps<T, U>
  : MatterClientAndMatterDetailsMatterProps<T, U>;

type MatterClientAndMatterDetailsRenderProps<E extends MatterType, F extends MatterFormMode> = {
  template: E;
  onDelete: () => Promise<void>;
} & MatterClientAndMatterDetailsProps<E, F>;

export const MatterClientAndMatterDetails = <T extends MatterType, U extends MatterFormMode>({
  matter,
  pmsName,
  practiceAreas,
  externalMatterData,
  onClientSave,
  onMatterUpdate,
  clientFilterData,
  feeEarnersCascaderRender,
  template,
  client,
  setCurrentClient,
  updateMatterLoading,
  onDelete,
}: MatterClientAndMatterDetailsRenderProps<T, U>): ReturnType<
  FC<MatterClientAndMatterDetailsRenderProps<T, U>>
> => {
  return (
    <div>
      <Spinner
        className={styles["spinner"]}
        show={updateMatterLoading === true}
        message={"Updating Matter..."}
      >
        <MatterEditorGenericTab>
          <div className={wrapper}>
            <MatterDetailsEditor
              template={template as MatterType}
              matter={matter as any}
              pmsName={pmsName}
              practiceAreas={practiceAreas || []}
              onMatterUpdate={onMatterUpdate as any}
              externalMatterData={externalMatterData as ExternalMatterData}
              clientFilterData={clientFilterData as ClientFilterData}
              onClientSave={onClientSave as OnClientSave}
              feeEarnersCascaderRender={feeEarnersCascaderRender}
              client={client}
              setCurrentClient={setCurrentClient}
              onDelete={onDelete}
            />
          </div>
        </MatterEditorGenericTab>
      </Spinner>
    </div>
  );
};
